// Pickers for S9: people, who are always Editors or Admins (GET /api/people?q=, tool-wide, at most 20 matches;
// poc CT-008). CT-026 removed the group picker with the widget's audience setting.
import type { PrincipalSummary } from '@feedback/shared/contracts/identity';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useId, useState } from 'react';
import { peopleQuery } from '@/lib/queries';
import { Button, Input } from '../ui/primitives';

const MIN_QUERY = 1;

/** Search the Editors and Admins for one person; calls onPick with the chosen person. */
export function PersonSearch({
  label,
  onPick,
  disabled,
  placeholder = 'Type a name',
}: {
  label: string;
  onPick: (person: PrincipalSummary) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const id = useId();
  const listId = `${id}-list`;
  const trimmed = q.trim();
  const people = useQuery({ ...peopleQuery(trimmed), enabled: trimmed.length >= MIN_QUERY });
  const matches = (people.data ?? []).filter((p) => p.kind === 'person');
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <Input
        id={id}
        value={q}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        aria-controls={listId}
        onChange={(e) => setQ(e.target.value)}
      />
      {trimmed.length >= MIN_QUERY ? (
        <ul
          id={listId}
          aria-label={`${label}: matches`}
          className="max-h-48 overflow-y-auto rounded-md border border-border"
        >
          {people.isPending ? <li className="p-2 text-sm text-muted">Searching…</li> : null}
          {people.data && matches.length === 0 ? <li className="p-2 text-sm text-muted">No one matches.</li> : null}
          {matches.map((person) => (
            <li key={person.principalId}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-surface-raised"
                onClick={() => {
                  onPick(person);
                  setQ('');
                }}
              >
                {person.displayName}
                {person.upn ? <span className="ml-2 text-xs text-muted">{person.upn}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Chosen people as removable chips. */
export function PeopleChips({
  people,
  onRemove,
  disabled,
}: {
  people: { principalId: string; displayName: string }[];
  onRemove: (principalId: string) => void;
  disabled?: boolean;
}) {
  if (people.length === 0) {
    return <p className="text-sm text-muted">No one chosen yet.</p>;
  }
  return (
    <ul className="flex flex-wrap gap-2">
      {people.map((person) => (
        <li
          key={person.principalId}
          className="inline-flex items-center gap-1 rounded-full border border-border-strong bg-surface-raised py-0.5 pr-1 pl-3 text-sm"
        >
          {person.displayName}
          {disabled ? null : (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-1"
              aria-label={`Remove ${person.displayName}`}
              onClick={() => onRemove(person.principalId)}
            >
              <X aria-hidden="true" className="size-3.5" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
